import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fadeUp, stagger } from "./motion";
import { scrollToSection } from "./SmoothScroll";
import { useLang } from "../i18n";

// clear-face yield % per NHLA grade, used for the board visual
const YIELDS = [83.3, 83.3, 66.7, 50];

const BoardVisual = ({ pct }) => (
  <div className="relative h-9 w-full overflow-hidden rounded-sm border border-brand-blue/25 bg-[repeating-linear-gradient(115deg,#E7E0D2_0px,#E7E0D2_10px,#DFD6C4_10px,#DFD6C4_20px)]">
    <div
      className="absolute inset-y-0 left-0 bg-brand-timber/90 transition-[width] duration-700"
      style={{ width: `${pct}%` }}
    />
    <span className="absolute inset-y-0 left-3 flex items-center font-mono text-[11px] font-bold uppercase tracking-wider text-brand-bone">
      {pct}%
    </span>
  </div>
);

export const GradeGuide = () => {
  const { t } = useLang();
  const items = t("grades.items");

  return (
    <section
      id="grades"
      data-testid="grade-guide"
      className="mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-36"
    >
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        className="mb-14 flex flex-col justify-between gap-8 md:flex-row md:items-end"
      >
        <div>
          <motion.span variants={fadeUp} className="font-mono text-xs uppercase tracking-[0.25em] text-brand-timber">
            {t("grades.kicker")}
          </motion.span>
          <motion.h2 variants={fadeUp} className="mt-4 max-w-2xl font-serif text-5xl font-medium leading-[0.95] tracking-tight text-brand-blue md:text-6xl">
            {t("grades.title")}
          </motion.h2>
        </div>
        <motion.p variants={fadeUp} className="max-w-sm font-light text-base leading-relaxed text-brand-slate">
          {t("grades.sub")}
        </motion.p>
      </motion.div>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4"
      >
        {items.map((g, i) => (
          <motion.div
            key={g.name}
            variants={fadeUp}
            data-testid={`grade-card-${i}`}
            className="flex flex-col rounded-sm border border-border bg-secondary/70 p-7 transition-colors duration-300 hover:border-brand-timber"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-slate">
              {g.full}
            </span>
            <h3 className="mt-1 font-serif text-3xl font-medium text-brand-blue md:text-4xl">
              {g.name}
            </h3>

            <div className="mt-6">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-timber">
                {t("grades.clear")}
              </p>
              <BoardVisual pct={YIELDS[i]} />
            </div>

            <p className="mt-5 flex-1 text-sm font-light leading-relaxed text-brand-slate">
              {g.desc}
            </p>

            <dl className="mt-6 space-y-3 border-t border-border pt-5">
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-timber">{t("grades.minBoard")}</dt>
                <dd className="mt-0.5 font-serif text-xl text-brand-blue">{g.size}</dd>
              </div>
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-timber">{t("grades.bestFor")}</dt>
                <dd className="mt-0.5 text-sm font-light text-brand-slate">{g.for}</dd>
              </div>
            </dl>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="mt-10 flex flex-col items-start justify-between gap-6 rounded-sm border border-border bg-secondary/40 px-7 py-6 md:flex-row md:items-center"
      >
        <p className="max-w-2xl text-sm font-light leading-relaxed text-brand-slate">
          {t("grades.note")}
        </p>
        <Link
          to="/#quote"
          data-testid="grades-quote-link"
          onClick={(e) => {
            e.preventDefault();
            scrollToSection("#quote");
          }}
          className="group inline-flex shrink-0 items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand-timber transition-colors hover:text-brand-blue"
        >
          {t("nav.quote")}
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </motion.div>
    </section>
  );
};
